<script setup lang="ts">
import StatusCard from '@components/RequestDetails/StatusCard.vue';

import { useAuthStore } from '@stores/useAuthStore';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { ACCEPT_TERMS_AND_CONDITIONS, GENERATE_TERMS_AND_CONDITIONS } from '@graphql/mutations/onboarding';
import { useMutation } from '@urql/vue';

import type { Record as RecordType } from '@graphql/types/documents';

interface Emits {
  (event: 'update:currentStep', value: number): void;
  (event: 'complete:onboarding'): void;
}

const emit = defineEmits<Emits>();

const { executeMutation: generateTermsAndConditions } = useMutation(GENERATE_TERMS_AND_CONDITIONS);
const { executeMutation: setTermsAndConditionsAcceptance } = useMutation(ACCEPT_TERMS_AND_CONDITIONS);

const { user, accessToken } = toRefs(useAuthStore());

const isFetching = ref(true);
const isTermsAccepted = ref(false);
const termsAndConditions = ref<RecordType | null>(null);

const acceptTermsAndConditions = async () => {
  const { error } = await setTermsAndConditionsAcceptance();

  if (error) {
    useToast({
      title: 'Er is iets misgegaan met het accepteren van de voorwaarden',
      message: 'Probeer het later opnieuw',
      theme: 'error',
    });
    return;
  }

  if (user.value?.onfido_checked) {
    emit('complete:onboarding');
  } else {
    emit('update:currentStep', 3);
  }
};

const downloadTermsAndConditions = () => {
  if (!termsAndConditions.value || !accessToken.value) return;

  downloadPdf(termsAndConditions.value.asset.absolute_url, accessToken.value);
};

onMounted(async () => {
  const { data, error } = await generateTermsAndConditions();

  isFetching.value = false;

  if (data.generateCombinedProcessingAgreement.record) {
    termsAndConditions.value = data.generateCombinedProcessingAgreement.record;
  } else if (error) {
    useToast({
      title: 'Er is iets misgegaan met het ophalen van de voorwaarden',
      message: 'Vernieuw de pagina en probeer het opnieuw',
      theme: 'error',
    });
  }
});
</script>

<template>
  <h2 class="text-2xl">Acceptatie voorwaarden</h2>
  <StatusCard v-if="isFetching" title="Moment we genereren de acceptatie voorwaarden" />
  <div v-else-if="termsAndConditions" class="flex flex-col gap-6">
    <p>
      Hieronder vind je de voorwaarden waarin wij aangeven hoe wij samen omgaan met uit te wisselen gegevens,
      informatie van jou en van je klant.
      De voorwaarden bestaat uit de gegevensuitwisselingsovereenkomst en de algemene voorwaarden die van toepassing zijn op onze samenwerking.
      Na je akkoord én je online identificatie, kun je aan de slag in de Intermediairsportal.
    </p>
    <UIRecord :record="termsAndConditions" downloadable @download="downloadTermsAndConditions" />
    <label class="flex">
      <UICheckbox
        v-model="isTermsAccepted"
        :value="isTermsAccepted"
        name="acceptTerms"
        label="Hiermee ga ik akkoord met de gegevensuitwisselingsovereenkomst en accepteer ik de algemene voorwaarden."
      />
    </label>
    <div class="flex justify-between items-center">
      <UIButton
        data-test-id="sign-previous-button"
        theme="tertiary"
        @click="emit('update:currentStep', 1)"
      >
        Vorige
      </UIButton>
      <UIButton
        data-test-id="accept-terms-button"
        :is-disabled="!isTermsAccepted"
        @click="acceptTermsAndConditions()"
      >
        Bevestigen
        <template #icon-end>
          <FontAwesomeIcon :icon="['fas', 'arrow-right']" size="1x" />
        </template>
      </UIButton>
    </div>
  </div>
  <div v-else>
    <div class="flex flex-col text-center gap-4 bg-gray-50 rounded-lg p-6">
      <FontAwesomeIcon :icon="['fad', 'circle-exclamation']" class="text-5xl fa-orange" />
      <strong>
        Er is iets misgegaan bij het ophalen van de acceptatie voorwaarden. Probeer het later opnieuw.
      </strong>
    </div>
  </div>
</template>
